import { forwardRef } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { LinkProps } from "react-router-dom";
import { getButtonClasses } from "./Button";
import type { ButtonVariant, ButtonSize } from "./Button";

export interface LinkButtonProps extends Omit<LinkProps, "className"> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Additional CSS classes */
  className?: string;
  /** Button contents */
  children: ReactNode;
}

/**
 * A React Router Link styled as a button.
 * Use this for navigation actions that should look like buttons.
 *
 * @example
 * ```tsx
 * <LinkButton to="/upgrade" variant="primary">Upgrade to Pro</LinkButton>
 * <LinkButton to="/settings" variant="secondary" size="sm">Settings</LinkButton>
 * ```
 */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ variant = "primary", size = "md", className, children, ...props }, ref) => {
    return (
      <Link ref={ref} className={getButtonClasses(variant, size, className)} {...props}>
        {children}
      </Link>
    );
  },
);

LinkButton.displayName = "LinkButton";
